import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import ReportIcon from "@mui/icons-material/Report";
import WarningIcon from "@mui/icons-material/Warning";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PropTypes from "prop-types";

const ToasterContext = createContext();

export const useToaster = () => useContext(ToasterContext);

const iconMap = {
  success: <TaskAltIcon sx={{ mr: 1 }} />,
  error: <ReportIcon sx={{ mr: 1 }} />,
  warning: <WarningIcon sx={{ mr: 1 }} />,
};

const colorMap = {
  success: "#76bc21",
  error: "#d1495b",
  warning: "#edae49",
  update: "#e5f6fc",
};

export const MuiToasterProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const handleClose = useCallback((id, _, reason) => {
    setToasts((prev) => {
      const targetToast = prev.find((toast) => toast.id === id);

      if (
        reason === "clickaway" ||
        (reason === "escapeKeyDown" && targetToast?.disableClose)
      ) {
        return prev;
      }

      return prev.map((toast) =>
        toast.id === id ? { ...toast, open: false } : toast,
      );
    });
  }, []);

  const showToast = useCallback(
    (
      type,
      message,
      id = Date.now(),
      autoHideDuration,
      disableClose = false,
      customIcon = null,
      sx = {},
      snackbarSx = {},
      reserveTopSlot = false,
    ) => {
      const resolvedId = id ?? Date.now();
      const defaultSnackbarSx = {
        right: "80px !important",
        left: "auto !important",
      };
      setToasts((prev) => {
        const existingIndex = prev.findIndex(
          (toast) => toast.id === resolvedId,
        );
        const nextToast = {
          open: true,
          type,
          message,
          id: resolvedId,
          autoHideDuration: autoHideDuration ?? 4000,
          disableClose,
          customIcon,
          sx,
          snackbarSx: { ...defaultSnackbarSx, ...snackbarSx },
          reserveTopSlot,
          anchorOrigin: { vertical: "top", horizontal: "right" },
        };

        if (existingIndex >= 0) {
          return prev.map((toast, index) =>
            index === existingIndex ? { ...toast, ...nextToast } : toast,
          );
        }
        return [...prev, nextToast];
      });
      return resolvedId;
    },
    [],
  );

  const updateToast = useCallback((id, message) => {
    setToasts((prev) =>
      prev.map((toast) => (toast.id === id ? { ...toast, message } : toast)),
    );
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((prev) =>
      prev.map((toast) => {
        if (id == null || toast.id === id) {
          return { ...toast, open: false };
        }
        return toast;
      }),
    );
  }, []);

  const handleExited = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const contextValue = useMemo(
    () => ({
      success: (
        msg,
        autoHideDuration,
        disableClose,
        customIcon,
        sx,
        snackbarSx,
        reserveTopSlot,
      ) =>
        showToast(
          "success",
          msg,
          Date.now(),
          autoHideDuration,
          disableClose,
          customIcon,
          sx,
          snackbarSx,
          reserveTopSlot,
        ),
      error: (
        msg,
        autoHideDuration,
        disableClose,
        customIcon,
        sx,
        snackbarSx,
        reserveTopSlot,
      ) =>
        showToast(
          "error",
          msg,
          Date.now(),
          autoHideDuration,
          disableClose,
          customIcon,
          sx,
          snackbarSx,
          reserveTopSlot,
        ),
      warning: (
        msg,
        autoHideDuration,
        disableClose,
        customIcon,
        sx,
        snackbarSx,
        reserveTopSlot,
      ) =>
        showToast(
          "warning",
          msg,
          Date.now(),
          autoHideDuration,
          disableClose,
          customIcon,
          sx,
          snackbarSx,
          reserveTopSlot,
        ),
      dismiss: (id) => dismissToast(id),

      update: (id, msg) => updateToast(id, msg),
    }),
    [showToast, updateToast, dismissToast],
  );

  return (
    <ToasterContext.Provider value={contextValue}>
      {children}
      {(() => {
        const hasReserveTopSlotToast = toasts.some(
          (item) => item.open && item.reserveTopSlot,
        );

        return toasts.map((toast) => {
          const stackBelowReservedSlotSx =
            hasReserveTopSlotToast && !toast.reserveTopSlot
              ? { top: "116px !important" }
              : {};

          return (
            <Snackbar
              key={toast.id}
              open={toast.open}
              autoHideDuration={
                toast.disableClose ? null : toast.autoHideDuration
              }
              onClose={(event, reason) => handleClose(toast.id, event, reason)}
              anchorOrigin={toast.anchorOrigin}
              // TransitionProps={{ onExited: () => handleExited(toast.id) }}
              slotProps={{
                transition: {
                  onExited: () => handleExited(toast.id),
                },
              }}
              sx={{
                zIndex: 2000,
                ...stackBelowReservedSlotSx,
                ...toast.snackbarSx,
              }}
            >
              <Alert
                severity={toast.type}
                variant="filled"
                icon={toast.customIcon ?? iconMap[toast.type]}
                onClose={
                  toast.disableClose
                    ? undefined
                    : (event, reason) => handleClose(toast.id, event, reason)
                }
                sx={{
                  width: "100%",
                  minWidth: { xs: "auto", sm: 400 },
                  backgroundColor: colorMap[toast.type],
                  color: "#fff",
                  alignItems: "center",
                  "& .MuiAlert-message > div": {
                    minWidth: { xs: "auto !important", sm: "300px !important" },
                    width: "100%",
                  },
                  ...toast.sx,
                }}
              >
                {toast.message}
              </Alert>
            </Snackbar>
          );
        });
      })()}
    </ToasterContext.Provider>
  );
};

MuiToasterProvider.propTypes = {
  children: PropTypes.node,
};
